// Caixas de oferta no checkout. Cada caixa leva uma parte das peças do carrinho; a soma de todas as caixas é sempre igual à quantidade de cada peça.
// Caixa: { baby, mom, message, qty: { [índice da linha do carrinho]: quantidade } }.
export const MAX_BOXES = 10;
// A primeira caixa de oferta está incluída; cada caixa seguinte custa isto (em euros). Tem de coincidir com GIFT_EXTRA_BOX_CENTS em worker/orders.ts.
export const GIFT_EXTRA_BOX_PRICE = 1;
export const giftFee = (gift) => (gift ? Math.max(0, gift.boxes.length - 1) * GIFT_EXTRA_BOX_PRICE : 0);
const blankBox = (qty = {}) => ({ baby: "", mom: "", message: "", qty });

// Uma caixa com tudo lá dentro: é o ponto de partida.
export const defaultGift = (items) => ({ boxes: [blankBox(Object.fromEntries(items.map((it, i) => [i, it.qty])))] });

// Se o carrinho mudou (quantidades, peças), acerta as caixas: o que falta vai para a primeira caixa, o que sobra sai da última.
export const fitGift = (gift, items) => {
  if (!gift) return gift;
  const boxes = gift.boxes.map((b) => ({ ...b, qty: { ...b.qty } }));
  items.forEach((it, i) => {
    let extra = boxes.reduce((s, b) => s + (b.qty[i] ?? 0), 0) - it.qty;
    for (let k = boxes.length - 1; extra > 0 && k >= 0; k--) {
      const take = Math.min(extra, boxes[k].qty[i] ?? 0);
      boxes[k].qty[i] = (boxes[k].qty[i] ?? 0) - take;
      extra -= take;
    }
    if (extra < 0) boxes[0].qty[i] = (boxes[0].qty[i] ?? 0) - extra;
  });
  return { boxes };
};

export const addBox = (gift) => (gift.boxes.length >= MAX_BOXES ? gift : { boxes: [...gift.boxes, blankBox()] });
export const removeBox = (gift, k) => {
  if (gift.boxes.length < 2) return gift;
  const rest = gift.boxes.filter((_, n) => n !== k).map((b) => ({ ...b, qty: { ...b.qty } }));
  for (const [i, q] of Object.entries(gift.boxes[k].qty)) rest[0].qty[i] = (rest[0].qty[i] ?? 0) + q;
  return { boxes: rest };
};
export const setBox = (gift, k, patch) => ({ boxes: gift.boxes.map((b, n) => (n === k ? { ...b, ...patch } : b)) });

// Mover uma unidade de uma peça para a caixa k (+1) ou tirá-la (-1). A unidade vem de, ou volta para, outra caixa: nunca se perde nem aparece nenhuma.
export const moveUnit = (gift, k, i, d) => {
  const boxes = gift.boxes.map((b) => ({ ...b, qty: { ...b.qty } }));
  const others = boxes.map((_, n) => n).filter((n) => n !== k);
  if (d > 0) {
    // Tira da primeira caixa que tenha a peça (a caixa 1 primeiro).
    const from = others.find((n) => (boxes[n].qty[i] ?? 0) > 0);
    if (from === undefined) return gift;
    boxes[from].qty[i] -= 1;
    boxes[k].qty[i] = (boxes[k].qty[i] ?? 0) + 1;
  } else {
    if ((boxes[k].qty[i] ?? 0) < 1 || !others.length) return gift;
    const to = others[0];
    boxes[k].qty[i] -= 1;
    boxes[to].qty[i] = (boxes[to].qty[i] ?? 0) + 1;
  }
  return { boxes };
};

export const canMove = (gift, k, i, d) => moveUnit(gift, k, i, d) !== gift;

export const giftProblem = (gift) => {
  if (!gift) return null;
  for (const [k, b] of gift.boxes.entries()) {
    if (!Object.values(b.qty).some((q) => q > 0)) return `A caixa ${k + 1} está vazia: passa lá alguma peça ou retira a caixa.`;
    if (!b.baby.trim() && !b.mom.trim()) return `Indica o nome do bebé ou da mamã na caixa ${k + 1}.`;
  }
  return null;
};

export const giftPayload = (gift) =>
  gift ? { boxes: gift.boxes.map((b) => ({ baby_name: b.baby.trim() || null, mom_name: b.mom.trim() || null, message: b.message.trim() || null, items: Object.entries(b.qty).filter(([, q]) => q > 0).map(([index, qty]) => ({ index: Number(index), qty })) })) } : null;
